export interface StatusGroup {
  id: string;
  statuses: string[];
  iconColor: string;
}

export const statusGroups: StatusGroup[] = [
  {
    // SPR-1054: request hasn't been sent to the office yet - held for 2 representative
    // signatures. Kept as its own group (not folded into PendingApproval) since it isn't in the
    // office's queue at all yet.
    id: 'PendingSignatures',
    statuses: ['PendingSignatures'],
    iconColor: 'var(--icon-status-1)',
  },
  {
    id: 'PendingApproval',
    statuses: [
      // SupportRequestInitialStatusEnum
      'PendingFirstReview',
      'PendingExceptionsCommitteeApproval',
      'PendingManagementApproval',
      // SupportRequestStatuses
      'PendingInitialApproval',
      'PendingExceptionsCommitteeApproval',
    ],
    iconColor: 'var(--icon-status-1)',
  },
  {
    id: 'PendingCompletion',
    statuses: [
      // SupportRequestInitialStatusEnum
      'PendingCompletion',
      'CompletionReturned',
      // SupportRequestStatuses
      'PendingCompletion',
      'CompletionReturned',
      'DonePendingCompletion',
      'DoneCompletionReturned',
    ],
    iconColor: 'var(--icon-status-2)',
  },
  {
    id: 'Approved',
    statuses: [
      // SupportRequestInitialStatusEnum
      'Approved',
      // SupportRequestStatuses
      'InitialApprovalApproved',
    ],
    iconColor: 'var(--icon-status-6)',
  },
  {
    id: 'PendingExecution',
    statuses: [
      // SupportRequestStatuses
      'PendingExecution',
    ],
    iconColor: 'var(--icon-status-3)',
  },
  {
    id: 'Rejected',
    statuses: [
      // SupportRequestInitialStatusEnum
      'Rejected',
      // SupportRequestStatuses
      'Rejected',
    ],
    iconColor: 'var(--icon-status-4)',
  },
  {
    id: 'Cancelled',
    statuses: [
      // SupportRequestInitialStatusEnum
      'Cancelled',
      // SupportRequestStatuses
      'Cancelled',
    ],
    iconColor: 'var(--icon-status-4)',
  },
  {
    id: 'PaymentReturned',
    statuses: [
      // SupportRequestStatuses
      'PaymentReturned',
    ],
    iconColor: 'var(--icon-status-2)',
  },
  {
    id: 'Finished',
    statuses: [
      // SupportRequestStatuses
      'Done',
      //virtual status that come from the aggregated function
      'Finished',
    ],
    iconColor: 'var(--icon-status-5)',
  },
];

export const statusToGroupMap: Record<string, StatusGroup> = {};
statusGroups.forEach((group) => {
  group.statuses.forEach((status) => {
    statusToGroupMap[status] = group;
  });
});

export const getStatusGroup = (status: string): StatusGroup | undefined => {
  return statusToGroupMap[status];
};

export const getStatusColor = (status: string): string => {
  const group = getStatusGroup(status);
  return group?.iconColor || 'var(--icon-status-1)';
};

/**
 * Calculates the aggregated display status from an array of statuses.
 * Works for both one-time (single element) and monthly (multiple elements) requests.
 *
 * Logic:
 * - Single status → return as-is (if not Done)
 * - All Rejected → 'Rejected'
 * - All Cancelled → 'Cancelled'
 * - All terminal (Done/Rejected/Cancelled) → 'Finished'
 * - Any month whose payment bounced → 'PaymentReturned'
 * - Otherwise → 'InitialApprovalApproved' (maps to Approved, until all months finish)
 */
export const getAggregatedStatus = (statuses: string[]): string => {
  if (statuses.length === 1) {
    return statuses[0];
  }

  if (statuses.every((s) => s === 'Rejected')) return 'Rejected';
  if (statuses.every((s) => s === 'Cancelled')) return 'Cancelled';

  // HAT-1395: a monthly request with one month bounced read «מאושר» like any other - the month the
  // coordinator has to act on was hidden behind the request's approved months.
  if (statuses.includes('PaymentReturned')) return 'PaymentReturned';

  const terminalStatuses = ['Done', 'Rejected', 'Cancelled', 'DonePendingCompletion', 'DoneCompletionReturned'];
  const allTerminal = statuses.every((s) => terminalStatuses.includes(s));

  if (allTerminal) {
    return 'Finished';
  }

  return 'InitialApprovalApproved'; 
};


/**
 * SPR-1205 — mobile status chevron bar (Figma 186:68 / 168:105).
 *
 * Exact Hebrew labels from design — do not translate or shorten.
 * Each item maps onto existing `statusGroups` ids; Archive unions the
 * terminal groups the management board already folds under "ארכיון".
 * Exceptions-committee is still stored under PendingApproval in
 * statusGroups, so that chevron carries an explicit status subset.
 */
export interface MobileStatusChevron {
  id: string;
  label: string;
  /** Existing statusGroups.id values this chevron represents. */
  groupIds: string[];
  /** Raw statuses selected when this chevron is toggled. */
  statuses: string[];
}

const statusesOf = (...groupIds: string[]): string[] =>
  statusGroups.filter((group) => groupIds.includes(group.id)).flatMap((group) => group.statuses);

const PENDING_APPROVAL_WITHOUT_EXCEPTIONS = statusesOf('PendingApproval').filter(
  (status) => status !== 'PendingExceptionsCommitteeApproval',
);

export const mobileStatusChevrons: MobileStatusChevron[] = [
  {
    id: 'PendingApproval',
    label: 'ממתין לאישור',
    groupIds: ['PendingApproval', 'PendingSignatures'],
    statuses: [...statusesOf('PendingSignatures'), ...PENDING_APPROVAL_WITHOUT_EXCEPTIONS],
  },
  {
    id: 'PendingExceptionsCommitteeApproval',
    label: 'ממתינה לאישור ועדת חריגים',
    groupIds: ['PendingApproval'],
    statuses: ['PendingExceptionsCommitteeApproval'],
  },
  {
    id: 'PendingCompletion',
    label: 'השלמת מסמכים',
    groupIds: ['PendingCompletion'],
    statuses: statusesOf('PendingCompletion'),
  },
  {
    id: 'Approved',
    label: 'מאושר',
    groupIds: ['Approved'],
    statuses: statusesOf('Approved'),
  },
  {
    id: 'PendingExecution',
    label: 'ממתינה לביצוע',
    groupIds: ['PendingExecution'],
    statuses: statusesOf('PendingExecution'),
  },
  {
    id: 'Archive',
    label: 'ארכיון',
    groupIds: ['Rejected', 'Cancelled', 'PaymentReturned', 'Finished'],
    statuses: statusesOf('Rejected', 'Cancelled', 'PaymentReturned', 'Finished'),
  },
];
